import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Logo, GradientBar, Button, Card, Badge, LoadingState, ErrorState } from "@/components/design-system";
import { getPublicProduct, CATEGORY_LABEL } from "@/lib/products";
import { listOpenCampaignsForProduct } from "@/lib/campaigns";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function PublicProduct() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading", product: null, err: "" });
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const product = await getPublicProduct(slug);
        setState({ status: "ready", product, err: "" });
        document.title = `${product.name} — SaaSLaunchingLab`;
        try {
          const cs = await listOpenCampaignsForProduct(product.id);
          setCampaigns(cs || []);
        } catch (_) { /* non-blocking */ }
      } catch (err) {
        const s = err?.response?.status;
        setState({ status: s === 404 ? "notfound" : "error", product: null, err: formatApiError(err) });
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-white text-[#0B1F3A]">
      <header className="border-b border-[#0B1F3A]/10">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/"><Logo variant="light" height={54} /></Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/app"><Button variant="primary">Open app</Button></Link>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost">Log in</Button></Link>
                <Link to="/register"><Button variant="primary">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
        <GradientBar />
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        {state.status === "loading" && <LoadingState label="Loading product…" />}
        {state.status === "notfound" && (
          <div className="text-center py-24">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Product not found</p>
            <h1 className="font-[Montserrat] font-extrabold text-4xl mt-2">This product isn't published — or doesn't exist.</h1>
            <div className="mt-6"><Link to="/"><Button variant="primary">Back to home</Button></Link></div>
          </div>
        )}
        {state.status === "error" && <ErrorState detail={state.err} />}
        {state.status === "ready" && state.product && (
          <>
            <div className="flex items-start gap-6">
              {state.product.logo_url ? (
                <img src={state.product.logo_url} alt="" className="w-24 h-24 rounded-2xl object-cover border border-[#0B1F3A]/10 bg-white" />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-[#E8F1FF] border border-[#0B1F3A]/10 flex items-center justify-center text-[#2D7FF9] font-[Montserrat] font-extrabold text-4xl">
                  {(state.product.name || "?").trim().charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="active">Live on SaaSLaunchingLab</Badge>
                  <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{CATEGORY_LABEL[state.product.category] || state.product.category}</span>
                </div>
                <h1 data-testid="public-product-name" className="font-[Montserrat] font-extrabold text-4xl md:text-5xl tracking-tight mt-3">{state.product.name}</h1>
                <p data-testid="public-product-tagline" className="mt-3 text-lg text-[#0B1F3A]/80">{state.product.tagline}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-[#0B1F3A]/70">
                  <span>By <b className="text-[#0B1F3A]">{state.product.founder_name}</b></span>
                  {state.product.website_url && (
                    <span className="inline-flex items-center gap-2">
                      <a href={state.product.website_url} target="_blank" rel="noreferrer" className="text-[#2D7FF9] font-semibold hover:underline" data-testid="public-product-website-link">Visit website ↗</a>
                      {state.product.website_verified_at && (
                        <span
                          data-testid="public-product-verified-badge"
                          className="inline-flex items-center gap-1 rounded-full bg-[#7ED957]/20 border border-[#7ED957]/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0B1F3A]"
                          title={`Website reachability and malware-DB check passed on ${new Date(state.product.website_verified_at).toISOString().slice(0, 10)}.`}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                          Verified safe · {new Date(state.product.website_verified_at).toISOString().slice(0, 10)}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <h2 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A]">About the product</h2>
                  <p data-testid="public-product-description" className="mt-3 text-[#0B1F3A]/85 leading-relaxed whitespace-pre-wrap">{state.product.description}</p>
                </Card>
              </div>
              <Card>
                <h3 className="font-[Montserrat] font-bold text-lg text-[#0B1F3A]">Help this launch</h3>
                <p className="mt-2 text-sm text-[#0B1F3A]/75">
                  {campaigns.length > 0 ? "This product has an open validation campaign — give structured feedback and earn reputation." : "No open campaigns right now. Check back soon."}
                </p>
                {campaigns.length > 0 && (
                  <div className="mt-4 space-y-2" data-testid="public-open-campaigns">
                    {campaigns.map((c) => (
                      <Link key={c.id} to={`/campaign/${c.id}`} data-testid={`public-campaign-${c.id}`} className="block rounded-xl border border-[#0B1F3A]/10 p-3 hover:border-[#2D7FF9] transition-colors">
                        <div className="flex items-center gap-2">
                          <Badge variant="active">Open</Badge>
                          <span className="text-[10px] uppercase tracking-widest text-[#0B1F3A]/60">{c.template_name}</span>
                        </div>
                        <div className="font-semibold text-[#0B1F3A] mt-1 text-sm">{c.name}</div>
                        <div className="text-xs text-[#0B1F3A]/60 mt-1">{c.response_count}{c.target_responses ? ` / ${c.target_responses}` : ""} responses</div>
                      </Link>
                    ))}
                  </div>
                )}
                <div className="mt-4">
                  {user ? (
                    <Link to="/app/explore"><Button variant="primary">Back to explore</Button></Link>
                  ) : (
                    <Link to={`/register?role=community`}><Button variant="primary">Join the community</Button></Link>
                  )}
                </div>
                <div className="mt-6 pt-6 border-t border-[#0B1F3A]/10 text-xs text-[#0B1F3A]/55 space-y-1">
                  <div>Published {new Date(state.product.published_at).toLocaleDateString()}</div>
                  <div>Category: {CATEGORY_LABEL[state.product.category] || state.product.category}</div>
                </div>
              </Card>
            </div>
          </>
        )}
      </main>

      <footer className="bg-[#0B1F3A] text-white mt-20">
        <GradientBar />
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
          <Logo variant="dark" height={40} />
          <span>© {new Date().getFullYear()} Pastoria Project SL · EU data residency · GDPR-first</span>
        </div>
      </footer>
    </div>
  );
}
